import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { IProvider } from 'src/app/models/provider.model';
import { AuthService } from 'src/app/services/auth.service';
import { ProviderService } from 'src/app/services/provider.service';
import { SearchService } from '../../services/search.service';
import { faAngleDoubleLeft, faAngleDoubleRight, faAngleLeft, faAngleRight, faPenToSquare, faSort, faSortDown, faSortUp, faTimes, faTrash } from '@fortawesome/free-solid-svg-icons';

@Component({
  selector: 'app-providers-v2',
  templateUrl: './providers.component.html',
  styleUrls: ['./providers.component.sass']
})
export class ProvidersComponent implements OnInit, OnDestroy {
  loading = false;
  pageSize = 10;
  currentPage = 0;
  totalPages = 1;
  totalElements = 0;
  sortBy = 'ASC';
  orderBy = 'name';
  searchTerm: string | undefined = undefined;
  providers: IProvider[] = [];
  private searchSubscription?: Subscription;

  // Permissões
  isRoot = false;

  // Delete modal
  showConfirmDeletePopup = false;
  providerToDelete = '';

  // Expor Math para o template
  Math = Math;

  pageSizeOptions = [
    { value: 10, label: '10' },
    { value: 20, label: '20' },
    { value: 50, label: '50' },
    { value: 100, label: '100' },
    { value: 1000, label: 'Todos' }
  ];

  faSort = faSort;
  faSortUp = faSortUp;
  faSortDown = faSortDown;
  faDelete = faTrash;
  faPenToSquare = faPenToSquare;
  faAngleDoubleLeft = faAngleDoubleLeft;
  faAngleLeft = faAngleLeft;
  faAngleRight = faAngleRight;
  faAngleDoubleRight = faAngleDoubleRight;
  faTimes = faTimes;

  constructor(
    private providerService: ProviderService,
    private router: Router,
    private searchService: SearchService,
    private authService: AuthService
  ) {}

  ngOnInit(): void {
    this.loading = true;
    this.isRoot = this.authService.getPermission() === 'ROOT';
    
    // Limpa o termo de busca ao entrar na página
    this.searchService.setSearchTerm('');
    
    // Carrega a lista inicial
    this.getProviders(0, this.pageSize);

    // Escuta mudanças no termo de pesquisa do header
    this.searchSubscription = this.searchService.searchTerm$.subscribe((term: string) => {
      this.searchTerm = term || undefined;
      this.getProviders(0, this.pageSize);
    });
  }

  ngOnDestroy(): void {
    this.searchSubscription?.unsubscribe();
  }

  toggleSort(column: string): void {
    if (this.orderBy === column) {
      this.sortBy = this.sortBy === 'ASC' ? 'DESC' : 'ASC';
    } else {
      this.orderBy = column;
      this.sortBy = 'ASC';
    }
    this.getProviders(0, this.pageSize);
  }

  getProviders(pageNumber: number, pageSize: number): void {
    this.loading = true;
    this.currentPage = pageNumber;
    this.providerService.listProviders(pageNumber, pageSize, this.sortBy, this.orderBy, this.searchTerm || '')
      .subscribe({
        next: (providersPaged) => {
          this.providers = providersPaged.data;
          if (providersPaged.page_info) {
            this.currentPage = providersPaged.page_info.current_page;
            this.totalPages = providersPaged.page_info.total_pages;
            this.totalElements = providersPaged.page_info.total_elements;
          }
          this.loading = false;
        },
        error: (error) => {
          console.error('Erro ao buscar prestadores:', error);
          this.loading = false;
        }
      });
  }

  goToFirstPage(): void {
    if (this.currentPage > 0) {
      this.getProviders(0, this.pageSize);
    }
  }

  goToPreviousPage(): void {
    if (this.currentPage > 0) {
      this.getProviders(this.currentPage - 1, this.pageSize);
    }
  }

  goToNextPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.getProviders(this.currentPage + 1, this.pageSize);
    }
  }

  goToLastPage(): void {
    if (this.currentPage < this.totalPages - 1) {
      this.getProviders(this.totalPages - 1, this.pageSize);
    }
  }

  onPageSizeChange(newSize: number): void {
    this.pageSize = newSize;
    this.getProviders(0, this.pageSize);
  }

  generatePagination(current: number, total: number): (string | number)[] {
    const range = (start: number, end: number): number[] => {
      return Array.from({ length: (end - start) }, (v, k) => k + start);
    };
    let pages: (string | number)[] = [1];
    let left = current - 1;
    let right = current + 1;
    if (left < 2) right += 2 - left;
    if (right > total - 1) left -= right - (total - 1);
    left = Math.max(2, left);
    right = Math.min(total - 1, right);
    if (left > 2) pages.push('...');
    pages = pages.concat(range(left, right + 1));
    if (right < total - 1) pages.push('...');
    if (total > 1 && total !== pages[pages.length - 1]) pages.push(total);
    return pages;
  }

  navigateTo(page: number | string): void {
    if (page !== '...') {
      this.getProviders(+page - 1, this.pageSize);
    }
  }

  edit(id?: string): void {
    this.router.navigateByUrl('/main/v2', { skipLocationChange: true }).then(() => {
      this.router.navigate(['main', 'v2', 'providers', id, 'edit']);
    });
  }

  showDeleteModal(id?: string): void {
    this.showConfirmDeletePopup = true;
    this.providerToDelete = id || '';
  }

  closeDeleteModal(): void {
    this.showConfirmDeletePopup = false;
    this.providerToDelete = '';
  }

  onDelete(): void {
    if (!this.providerToDelete) return;

    this.loading = true;
    this.showConfirmDeletePopup = false;

    this.providerService.deleteProvider(this.providerToDelete)
      .subscribe({
        next: () => {
          this.providerToDelete = '';
          this.getProviders(this.currentPage, this.pageSize);
        },
        error: (error) => {
          console.error('Erro ao deletar prestador:', error);
          this.loading = false;
          this.providerToDelete = '';
        }
      });
  }
}
